import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import EmptyState from '../EmptyState';
import { formatCurrency, formatYAxisValue } from './formatters';
import { DailyEvolutionData } from './types';

interface DrillDownDailyEvolutionChartProps {
  evolucao_diaria: DailyEvolutionData[];
  title?: string;
  valueKey?: keyof DailyEvolutionData;
  valueLabel?: string;
  color?: string;
  height?: number;
}

const DrillDownDailyEvolutionChart: React.FC<DrillDownDailyEvolutionChartProps> = ({
  evolucao_diaria,
  title = "Evolução Diária",
  valueKey = "valor_compras",
  valueLabel = "Valor (R$)",
  color,
  height = 400
}) => {
  const theme = useTheme();
  
  // Verificar se há dados válidos
  if (!evolucao_diaria || evolucao_diaria.length === 0) {
    return (
      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <EmptyState
              icon="📈"
              title="Dados de Evolução Diária Indisponíveis"
              description="Não há dados suficientes para gerar o gráfico de evolução diária"
              height={height}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Preparar dados para o gráfico
  const chartData = [
    {
      id: valueKey === 'valor_compras' ? 'Valor Compras' : 'Valores',
      data: evolucao_diaria.map(item => ({
        x: `${item.dia}`,
        y: item[valueKey] || 0
      }))
    }
  ];

  // Definir cor do gráfico
  const chartColor = color || theme.palette.primary.main;

  return (
    <Grid item xs={12}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography 
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {title}
          </Typography>
          <Box sx={{ height }}>
            <ResponsiveLine
              data={chartData}
              margin={{ top: 50, right: 110, bottom: 50, left: 80 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Dia do Mês',
                legendOffset: 36,
                legendPosition: 'middle'
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: valueLabel,
                legendOffset: -60,
                legendPosition: 'middle',
                format: valueKey === 'valor_compras' ? formatYAxisValue : formatYAxisValue
              }}
              pointSize={8}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabelYOffset={-12}
              useMesh={true}
              colors={[chartColor]}
              tooltip={({ point }) => (
                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Dia {String(point.data.x)}
                  </Typography>
                  <Typography variant="body2">
                    {valueKey === 'valor_compras' 
                      ? formatCurrency(point.data.y as number)
                      : String(point.data.y)
                    }
                  </Typography>
                </Box>
              )}
              animate={true}
              motionConfig="gentle"
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default DrillDownDailyEvolutionChart; 